import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { StrictDateRange } from '../../../../../commonUI/DateRangeFilterSelect/types';
import { useBlueberryColorRawCounts } from './useBlueberryStageRawCounts';

type RawCountItem = {
  block_id: string;
  block_name: string;
  scan_date: string;
  stage_id: string;
  stage_name: string;
  stage_count: number;
  order?: number;
};

type BlockData = {
  blockId: string;
  blockName: string;
  stages: Array<{
    id: string;
    blockName: string;
    stageName: string;
    count: number;
    order: number;
  }>;
};

type GroupedRawCounts = {
  scanDate: string;
  displayDate: string;
  blocks: BlockData[];
};

export const useGroupedRawCounts = (
  customerId: string | undefined,
  dateRange: StrictDateRange | undefined
) => {
  const { rawCounts, isRawCountsLoading } = useBlueberryColorRawCounts(
    customerId,
    dateRange
  );

  const groupedData = useMemo(() => {
    if (!rawCounts?.length) return [];

    // Group by scan date first
    const dateGroups = rawCounts.reduce<Record<string, RawCountItem[]>>(
      (acc, item) => {
        const scanDate = item.scan_date;
        if (!acc[scanDate]) {
          acc[scanDate] = [];
        }
        acc[scanDate].push(item);
        return acc;
      },
      {}
    );

    // Process each date group
    const result: GroupedRawCounts[] = Object.entries(dateGroups).map(
      ([scanDate, items]) => {
        // Group by block within each date
        const blockGroups = items.reduce((acc, item) => {
          const blockId = item.block_id;
          if (!acc[blockId]) {
            acc[blockId] = {
              blockId: item.block_id,
              blockName: item.block_name,
              stages: [],
            };
          }

          acc[blockId].stages.push({
            id: `${item.block_name}-${item.stage_name}`,
            blockName: item.block_name,
            stageName: item.stage_name,
            count: item.stage_count,
            order: item.order || 999, // fallback for stages without order
          });

          return acc;
        }, {} as Record<string, BlockData>);

        // Sort stages within each block by order
        Object.values(blockGroups).forEach((block) => {
          block.stages.sort((a, b) => a.order - b.order);
        });

        return {
          scanDate,
          displayDate: format(parseISO(scanDate), 'MMMM dd, yyyy'),
          blocks: Object.values(blockGroups),
        };
      }
    );

    // Sort by scan date (newest first)
    result.sort(
      (a, b) => new Date(b.scanDate).getTime() - new Date(a.scanDate).getTime()
    );

    return result;
  }, [rawCounts]);

  return {
    groupedRawCounts: groupedData,
    isLoading: isRawCountsLoading,
  };
};
